import { GridifyFilter } from '@/constants/GridifyFilter';
import router from '@/router/router';
import { UserStore } from '@/stores/userStore';
export enum LogicalOperator {
	And = ',',
	Or = '|',
}

export enum ConditionalOperator {
	Equal = '=',
	NotEqual = '!=',
	LessThan = '<',
	GreaterThan = '>',
	GreaterThanOrEqual = '>=',
	LessThanOrEqual = '<=',
	Contains = '=*',
	NotContains = '!*',
	StartsWith = '^',
	NotStartsWith = '!^',
	EndsWith = '$',
	NotEndsWith = '!$',
}

export interface IGridifyFilter {
	field?: string;
	operator?: string;
	value?: string | number | boolean;
	connector?: string;
	nestedFilters?: IGridifyFilter[];
}

export interface IGridifyQuery {
	page: number;
	pageSize: number;
	orderBy: string | null | undefined;
	filter?: string;
}

interface IGridifyQueryBuilderOptions {
	useLocalStorage?: boolean;
	storageScope?: string;
	defaultOrderBy?: {
		field: string;
		descending?: boolean;
	};
}

export class GridifyQueryBuilder {
	private query: IGridifyQuery = {
		page: 1,
		pageSize: 20,
		orderBy: '',
		filter: '',
	};

	private orderByGroup = {} as {
		[key: string]: string;
	};

	private filteringExpressions: IExpression[] = [];
	private readonly useLocalStorage: boolean;
	private readonly localStorageKey: string;
	private readonly defaultOrderBy?: {
		field: string;
		descending?: boolean;
	};

	public static get _localStorageKey() {
		return GridifyQueryBuilder.buildLocalStorageKey();
	}

	public static buildLocalStorageKey(storageScope?: string) {
		const user = UserStore();
		const version = 'v1';
		const currentPath = storageScope || router.currentRoute.value.path;
		const path = currentPath.replace(/\//g, '______');
		return `${version}:${user.userClaims.Id}:${path}_config`;
	}

	constructor(options: boolean | IGridifyQueryBuilderOptions = true) {
		const parsedOptions: IGridifyQueryBuilderOptions =
			typeof options === 'boolean' ? { useLocalStorage: options } : options;
		this.useLocalStorage = parsedOptions.useLocalStorage ?? true;
		this.localStorageKey = GridifyQueryBuilder.buildLocalStorageKey(parsedOptions.storageScope);
		this.defaultOrderBy = parsedOptions.defaultOrderBy;

		const rawSavedQuery =
			this.useLocalStorage && localStorage.getItem(this.localStorageKey) != undefined
				? localStorage.getItem(this.localStorageKey)
				: null;

		let querySaved = null;
		if (rawSavedQuery) {
			try {
				querySaved = JSON.parse(rawSavedQuery);
			} catch {
				localStorage.removeItem(this.localStorageKey);
			}
		}

		if (querySaved) {
			this.query = querySaved.query;
			this.filteringExpressions = querySaved.filteringExpressions;
			this.orderByGroup = querySaved.orderByGroup;
		} else {
			this.applyDefaultOrderBy();
		}
	}

	private applyDefaultOrderBy() {
		if (!this.defaultOrderBy?.field) return;
		this.addOrderBy(this.defaultOrderBy.field, this.defaultOrderBy.descending);
	}

	setPage(page: number): GridifyQueryBuilder {
		this.query.page = page;
		return this;
	}

	setPageSize(pageSize: number): GridifyQueryBuilder {
		this.query.pageSize = pageSize;
		return this;
	}

	hasOrderBy(field: string) {
		return !!this.orderByGroup[field];
	}

	addOrderBy(field: string, descending: boolean = false): GridifyQueryBuilder {
		this.orderByGroup = {};
		const expression = `${field.trim()} ${descending ? 'desc' : ''}`.trim();
		this.orderByGroup[field] = expression;
		return this;
	}

	hasCondition(field: string) {
		return this.filteringExpressions.some((exp) => exp.value.startsWith(field));
	}

	addCondition(
		field: string,
		operator: GridifyFilter | ConditionalOperator,
		value: string | number | boolean | undefined | null,
		caseSensitive: boolean = true,
		escapeValue: boolean = true,
		override = false,
	): GridifyQueryBuilder {
		let filterValue = value;
		if (escapeValue && typeof value === 'string') {
			filterValue = value.replace(/([(),|]|\/i)/g, '\\$1');
		}
		if (value == undefined) {
			this.filteringExpressions = this.filteringExpressions.filter((exp) => exp.field !== field);
			return this;
		}
		if (!caseSensitive && value) {
			filterValue = `${String(filterValue)}/i`;
		}
		const filterExpression = `${field.trim()}${operator}${filterValue}`;
		let existingIndex = this.filteringExpressions.findIndex((expr) =>
			expr.value.startsWith(filterExpression),
		);
		if (override) {
			existingIndex = this.filteringExpressions.findIndex((exp) => exp.field == field);
		}
		if (existingIndex !== -1) {
			const existingExpression = this.filteringExpressions[existingIndex];
			if (existingExpression) {
				existingExpression.value = filterExpression;
			}
		} else {
			this.filteringExpressions.push({
				field: field,
				fieldValue: value,
				value: filterExpression,
				type: 'filter',
			});
		}
		this.removeTrailingAnd();
		return this;
	}

	addInCondition(
		field: string,
		values: (string | number | boolean)[],
		override = false,
	): GridifyQueryBuilder {
		if (!values || values.length === 0) {
			this.removeCondition(field);
			return this;
		}

		// Remove any existing conditions for this field
		this.removeCondition(field);

		// Create a single condition with all values joined by OR
		const conditions = values.map((value) => {
			const escapedValue =
				typeof value === 'string' ? value.replace(/([(),|]|\/i)/g, '\\$1') : value;
			return `${field.trim()}=${escapedValue}`;
		});

		const filterExpression = `(${conditions.join('|')})`;

		this.filteringExpressions.push({
			field: field,
			fieldValue: values.join(','), // Convert array to string for fieldValue
			value: filterExpression,
			type: 'filter',
		});

		return this;
	}

	removeCondition(field: string): GridifyQueryBuilder {
		this.filteringExpressions = this.filteringExpressions.filter((exp) => exp.field !== field);
		return this;
	}

	isOrderByEmpty() {
		return Object.keys(this.orderByGroup).length === 0;
	}

	private removeTrailingAnd() {
		if (this.filteringExpressions.length > 0) {
			const lastExpression = this.filteringExpressions[this.filteringExpressions.length - 1];
			if (!lastExpression) return;
			if (
				lastExpression.value.trim().toUpperCase() === LogicalOperator.And ||
				lastExpression.value.trim().toUpperCase() === LogicalOperator.Or
			) {
				this.filteringExpressions.pop();
			}
		}
	}

	startGroup(): GridifyQueryBuilder {
		this.filteringExpressions.push({ value: '(', type: 'startGroup' });
		return this;
	}

	endGroup(): GridifyQueryBuilder {
		this.filteringExpressions.push({ value: ')', type: 'endGroup' });
		return this;
	}

	and(): GridifyQueryBuilder {
		this.filteringExpressions.push({ value: LogicalOperator.And, type: 'op' });
		return this;
	}

	or(): GridifyQueryBuilder {
		this.filteringExpressions.push({ value: LogicalOperator.Or, type: 'op' });
		return this;
	}

	rebuild(): GridifyQueryBuilder {
		this.query.filter = '';
		this.query.orderBy = '';
		return this;
	}

	reset() {
		this.filteringExpressions = [];
		this.query.filter = '';
		this.query.orderBy = '';
		this.orderByGroup = {};
		this.applyDefaultOrderBy();
		this.setPage(1);
		this.saveState();
		return this;
	}

	saveState() {
		if (!this.useLocalStorage) return;

		localStorage.setItem(
			this.localStorageKey,
			JSON.stringify({
				query: this.query,
				filteringExpressions: this.filteringExpressions,
				orderByGroup: this.orderByGroup,
			}),
		);
	}

	build(): IGridifyQuery {
		let previousType: 'filter' | 'op' | 'startGroup' | 'endGroup' | null = null;
		let groupCounter = 0;
		this.query.filter = '';
		this.filteringExpressions.forEach((exp, index) => {
			if (exp.type === 'startGroup') {
				if (previousType === 'filter' || previousType === 'endGroup') {
					this.query.filter += LogicalOperator.And;
				}
				this.query.filter += '(';
				groupCounter++;
			} else if (exp.type === 'endGroup') {
				this.query.filter += ')';
				groupCounter--;
			} else if (exp.type === 'filter') {
				if (previousType === 'filter' || previousType === 'endGroup') {
					if (this.filteringExpressions[index - 1]?.type !== 'op') {
						this.query.filter += LogicalOperator.And;
					}
				}
				this.query.filter += exp.value;
			} else if (exp.type === 'op') {
				this.query.filter += exp.value;
			}
			if (this.query.filter?.endsWith(',()')) {
				this.query.filter = this.query.filter.replace(',()', '');
			}
			previousType = exp.type;
		});
		if (groupCounter !== 0) {
			throw new Error('Unbalanced groups in query filter');
		}
		try {
			this.validate();
		} catch (e) {
			//console.log(this.query.filter);
		}
		this.query.orderBy = Object.keys(this.orderByGroup)
			.map((item) => this.orderByGroup[item])
			.join(', ');
		this.query.filter = this.query.filter.replace('id=*', 'id=');
		this.saveState();
		return this.query;
	}

	validate() {
		this.filteringExpressions.forEach((exp) => {
			const previousType: 'filter' | 'op' | 'startGroup' | 'endGroup' | null = null;
			if (previousType === null && exp.type === 'op') {
				throw new Error('expression cannot start with a logical operator');
			}
			if (previousType === 'filter' && exp.type === 'filter') {
				throw new Error(
					'consecutive conditions are not allowed, consider adding a logical operator',
				);
			}
			if (previousType === 'op' && exp.type === 'op') {
				throw new Error('consecutive operators are not allowed, consider adding a filter');
			}
			if (previousType === 'startGroup' && exp.type === 'op') {
				throw new Error('logical operator immediately after startGroup is not allowed');
			}
			if (previousType === 'endGroup' && exp.type === 'filter') {
				throw new Error('Missing logical operator after endGroup');
			}
			if (previousType === 'startGroup' && exp.type === 'endGroup') {
				throw new Error('Empty groups are not allowed');
			}
			if (previousType === 'endGroup' && exp.type === 'startGroup') {
				throw new Error('Missing a logical operator between groups');
			}
		});
	}

	removeDuplicateItems(input: string): string {
		const items = input.split(',');
		const uniqueItems = [...new Set(items)];
		return uniqueItems.join(',');
	}

	getFilters() {
		return this.filteringExpressions.filter((c) => c.type === 'filter');
	}
}

export interface IExpression {
	field?: string;
	fieldValue?: string | number | boolean;
	value: string;
	type: 'filter' | 'op' | 'startGroup' | 'endGroup';
}
